import {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import styled from "styled-components";
import IconButton from "../Buttons/IconButton";
import { FilterContext } from "./FilterContext";
import { ReactComponent as ArrowIcon } from "../../assets/arrow.svg";
import { ReactComponent as FilterIcon } from "../../assets/filter.svg";
import Button from "../Buttons/Button";
import { getWindowHeight } from "../../utils";

function FilterModal({ onDismiss }: { onDismiss: Function }) {
  const filters = useContext(FilterContext);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const modalElement = ref.current;
    const preventTouch = (e: TouchEvent) => {
      // Check if the touch event's target is within an element that should allow swiping
      if (
        !e.target ||
        !(e.target instanceof Element) ||
        !e.target.closest(".swipe")
      ) {
        e.preventDefault();
      }
      // Not stopping propagation as we only want to selectively prevent the default behavior
    };
    // Add event listeners to prevent scrolling and propagation
    modalElement &&
      modalElement.addEventListener("touchmove", preventTouch, {
        passive: false,
      });

    return () => {
      // Clean up event listeners
      modalElement &&
        modalElement.removeEventListener("touchmove", preventTouch);
    };
  }, []);
  return (
    <FilterBackdrop onClick={() => onDismiss()} ref={ref}>
      <FilterContainer onClick={(e) => e?.stopPropagation()}>
        <div>
          <FilterHeader>
            <FilterModalTitle>
              <FilterIcon
                fill="black"
                width={15}
                height={15}
                style={{ paddingRight: 8 }}
              />
              Filters
            </FilterModalTitle>
            <IconButton
              icon="cross"
              onClick={() => onDismiss()}
              tooltip="Sluit filter opties"
            />
          </FilterHeader>
          <FilterBody>
            <FilterAccordion
              isOpen={false}
              title="Beheerjaar"
              isActive={filters.years !== undefined && filters.years.length > 0}
              onClick={() => null}
            >
              <YearFilter />
            </FilterAccordion>
            <FilterAccordion
              isOpen={false}
              title="Deelnemer"
              isActive={
                filters.members !== undefined && filters.members.length > 0
              }
              onClick={() => null}
            >
              <MemberFilter />
            </FilterAccordion>

            <FilterAccordion
              isOpen={false}
              title="Beheerpakket"
              isActive={
                filters.projects !== undefined && filters.projects.length > 0
              }
              onClick={() => null}
            >
              <ProjectFilter />
            </FilterAccordion>

            <FilterAccordion
              isOpen={false}
              title="Ingepland op"
              isActive={
                filters.plannedDates !== undefined &&
                filters.plannedDates.length > 0
              }
              onClick={() => null}
            >
              <PlannedDateFilter />
            </FilterAccordion>
          </FilterBody>
        </div>
        <FilterResetButton />
      </FilterContainer>
    </FilterBackdrop>
  );
}

function YearFilter() {
  const filters = useContext(FilterContext);
  const [, startTransition] = useTransition();
  return (
    <>
      {filters.allYears?.map((year) => (
        <OptionCheckbox<number>
          key={`${year}`}
          value={year}
          label={`${year}`}
          selected={Boolean(filters?.years && filters?.years?.includes(year))}
          onSelect={(year: number) => {
            startTransition(() => {
              let newOptions = [];
              if (filters?.years?.includes(year)) {
                newOptions = filters?.years?.filter((item) => item !== year);
              } else {
                newOptions = [...(filters?.years || []), year];
              }
              filters.setYears && filters.setYears(newOptions);
            });
          }}
        />
      ))}
    </>
  );
}
function MemberFilter() {
  const filters = useContext(FilterContext);
  const [, startTransition] = useTransition();
  return (
    <>
      {filters?.allMembers?.map(({ name, id }) => (
        <OptionCheckbox<string>
          key={id}
          label={name}
          value={id}
          selected={Boolean(
            filters?.members?.length && filters?.members?.includes(id)
          )}
          onSelect={(id) => {
            startTransition(() => {
              let newOptions = [];
              if (filters?.members?.includes(id)) {
                newOptions = filters?.members?.filter((item) => item !== id);
              } else {
                newOptions = [...(filters?.members || []), id];
              }
              filters.setMembers && filters.setMembers(newOptions);
            });
          }}
        />
      ))}
    </>
  );
}

function ProjectFilter() {
  const filters = useContext(FilterContext);
  const [, startTransition] = useTransition();
  return (
    <>
      {filters?.allProjects?.map((code) => (
        <OptionCheckbox<string>
          key={code}
          label={code}
          value={code}
          selected={Boolean(
            filters?.projects?.length && filters?.projects?.includes(code)
          )}
          onSelect={(code) => {
            startTransition(() => {
              let newOptions = [];
              if (filters?.projects?.includes(code)) {
                newOptions = filters?.projects?.filter((item) => item !== code);
              } else {
                newOptions = [...(filters?.projects || []), code];
              }
              filters.setProjects && filters.setProjects(newOptions);
            });
          }}
        />
      ))}
    </>
  );
}

function PlannedDateFilter() {
  const filters = useContext(FilterContext);
  const [, startTransition] = useTransition();
  return (
    <>
      {filters?.allPlannedDates?.map((date) => (
        <OptionCheckbox<string>
          key={`${date.date}`}
          value={date.date}
          label={new Date(date.date)
            .toJSON()
            .slice(0, 10)
            .split("-")
            .reverse()
            .join("-")}
          selected={Boolean(
            filters?.plannedDates?.length &&
              filters?.plannedDates?.includes(date.date)
          )}
          onSelect={(date: string) => {
            startTransition(() => {
              let newOptions = [];
              if (filters?.plannedDates?.includes(date)) {
                newOptions = filters?.plannedDates?.filter(
                  (item) => item !== date
                );
              } else {
                newOptions = [...(filters?.plannedDates || []), date];
              }
              filters.setPlannedDates && filters.setPlannedDates(newOptions);
            });
          }}
        />
      ))}
    </>
  );
}

const FilterResetButton = () => {
  const filters = useContext(FilterContext);
  const [, startTransition] = useTransition();
  const filtersActive = useMemo(
    () =>
      Boolean(
        filters?.members?.length ||
          filters?.years?.length ||
          filters?.projects?.length ||
          filters?.plannedDates?.length
      ),
    [filters]
  );
  return (
    <Button
      label="Reset filters"
      size="medium"
      disabled={!filtersActive}
      bordered
      onClick={() => startTransition(() => filters.resetFilters())}
    />
  );
};

interface FilterAccordionProps {
  title: string;
  children?: ReactNode;
  isOpen?: boolean;
  isActive?: boolean;
  onClick: Function;
}

const Indicator = styled.span`
  position: relative;
  transform: translate(50%, -50%);
  padding: 0.5em;
  color: white;
  display: flex;
  justify-content: center;
  font-size: 0.75em;
  background-color: orangered;
  border-radius: 50%;
  border: 1px solid white;
  align-items: center;
  left: -82px;
  top: 5px;
`;

const FilterAccordion = ({
  title,
  children,
  isActive,
  isOpen,
}: FilterAccordionProps) => {
  const [open, toggleOpen] = useState(Boolean(isOpen));
  const [, startTransition] = useTransition();
  return (
    <AccordionSection>
      <AccordionTitle onClick={() => startTransition(() => toggleOpen(!open))}>
        {title} {isActive ? <Indicator /> : null}
        <IconContainer isOpen={open}>
          <ArrowIcon />
        </IconContainer>
      </AccordionTitle>
      <AccordionContent isOpen={open} className="swipe">
        {open ? children : null}
      </AccordionContent>
    </AccordionSection>
  );
};

interface OptionProps<T> {
  selected: boolean;
  onSelect: (value: T) => void;
  label: string;
  value: T;
}

const OptionCheckbox = <T extends {}>({
  selected,
  onSelect,
  label,
  value,
}: OptionProps<T>) => {
  const handleCheckboxChange = () => {
    onSelect(value);
  };

  return (
    <OptionContainer selected={selected} onClick={handleCheckboxChange}>
      <Checkbox
        type="checkbox"
        checked={selected}
        onChange={handleCheckboxChange}
      />
      <OptionLabel>{label}</OptionLabel>
    </OptionContainer>
  );
};

const FilterModalTitle = styled.span`
  //styleName: Tablet/Text bold;
  font-family: Roboto;
  font-size: 16px;
  font-weight: 700;
  line-height: 19.2px;
  text-align: left;
  padding-top: 8px;
  padding-bottom: 16px;
`;

const FilterContainer = styled.div`
  width: 300px;
  overflow-x: auto;
  overflow-y: auto;
  background-color: #fafafa;
  border-radius: 16px;
  padding: 16px;
  position: absolute;
  top: 16px;
  bottom: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const FilterBackdrop = styled.div`
  z-index: 999;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-content: center;
  background: rgba(0, 0, 0, 0.5);
`;

const FilterHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const FilterBody = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-height: calc(${getWindowHeight()} - 160px);
  gap: 8px;
`;

const OptionContainer = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 12px;
`;

const Checkbox = styled.input`
  margin-right: 8px;
  width: 22px;
  height: 22px;
  cursor: pointer;
`;

const OptionLabel = styled.label`
  margin: 0;
  cursor: pointer;
`;

const AccordionSection = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border-radius: 5px;
`;

const AccordionTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #212529;
  line-height: 120%; /* 16.8px */
  cursor: pointer;
  padding: 9px;
  padding-top: 16px;
  padding-bottom: 16px;
  //styleName: Tablet/Label bold;
  font-family: Roboto;
  font-size: 13px;
  font-weight: 700;
  line-height: 15.6px;
  text-align: left;
`;

const AccordionContent = styled.div<{ isOpen: boolean }>`
  overflow-y: auto;
  max-height: ${({ isOpen }) => {
    return isOpen ? "180px" : "0px";
  }};
`;

const IconContainer = styled.div<{ isOpen: boolean }>`
  transition: transform 0.1s ease;
  transform: rotate(${({ isOpen }) => (isOpen ? "180deg" : 0)});
`;

export default FilterModal;
